import React, { useState } from "react";
import { AlertTriangle, HelpCircle, Trash2 } from "lucide-react";
import CustomModal from "../CustomModal/CustomModal";
import CustomButton from "../CustomButton/CustomButton";
import "../CustomTheme/customTheme.css";
import "./CustomConfirmDialog.css";

const VARIANTS = {
  danger: { icon: Trash2, button: "danger-solid", confirmText: "Delete" },
  warning: { icon: AlertTriangle, button: "primary", confirmText: "Continue" },
  primary: { icon: HelpCircle, button: "primary", confirmText: "Confirm" },
};

/**
 * CustomConfirmDialog — "Are you sure?" before an action.
 *
 *   <CustomConfirmDialog
 *     open={Boolean(rowToDelete)}
 *     message={`Delete "${rowToDelete?.name}"? This can't be undone.`}
 *     onConfirm={() => dispatch(deleteItem(rowToDelete._id))}   // may return a Promise
 *     onCancel={() => setRowToDelete(null)}
 *   />
 *
 * Props:
 *  - open, onConfirm, onCancel
 *  - title         default "Are you sure?"
 *  - message       body text (string or element)
 *  - variant       "danger" (default, red) | "warning" | "primary"
 *  - confirmText / cancelText
 *  - loading       controlled spinner; otherwise shown while onConfirm's Promise runs
 *  - closeOnConfirm   close automatically after onConfirm resolves (default true)
 */
const CustomConfirmDialog = ({
  open,
  onConfirm,
  onCancel,
  title = "Are you sure?",
  message,
  variant = "danger",
  confirmText,
  cancelText = "Cancel",
  loading: loadingProp,
  closeOnConfirm = true,
}) => {
  const [busy, setBusy] = useState(false);
  const config = VARIANTS[variant] || VARIANTS.danger;
  const Icon = config.icon;
  const loading = loadingProp ?? busy;

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm?.();
      if (closeOnConfirm) onCancel?.();
    } catch (_) {
      // Keep the dialog open so the user can retry; the action shows its own error
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    if (!loading) onCancel?.();
  };

  return (
    <CustomModal
      open={open}
      onClose={cancel}
      size="sm"
      showClose={false}
      closeOnBackdrop={!loading}
      closeOnEsc={!loading}
      footer={
        <>
          <CustomButton variant="outline" onClick={cancel} disabled={loading}>
            {cancelText}
          </CustomButton>
          <CustomButton variant={config.button} onClick={handleConfirm} loading={loading}>
            {confirmText || config.confirmText}
          </CustomButton>
        </>
      }
    >
      <div className={`ccd-content ccd-${variant}`}>
        <span className="ccd-icon" aria-hidden="true">
          <Icon size={22} />
        </span>
        <div className="ccd-text">
          <h4 className="ccd-title">{title}</h4>
          {message && <div className="ccd-message">{message}</div>}
        </div>
      </div>
    </CustomModal>
  );
};

export default CustomConfirmDialog;
